/// <amd-module name="Dashboards/Samples/WidgetHostSample" />
import * as React from "react";

import { WidgetSizeConverter } from "Dashboards/Grid/WidgetSizeConverter";
import { WidgetCatalogQuery } from "Dashboards/WidgetCatalog/WidgetCatalogQuery";
import { ICacheableQueryService } from "Dashboards/WidgetCommon/DashboardsCacheableQueryService";
import { DashboardWidgetProps } from "Dashboards/WidgetContracts/Internal/DashboardWidgetProps";
import { WidgetCatalogEntry } from "Dashboards/WidgetContracts/Internal/WidgetCatalogEntry";
import { WidgetCopyProps, WidgetHostLoadedEventInfo, WidgetHostProps } from "Dashboards/WidgetContracts/Internal/WidgetHostProps";
import { WidgetPresentationMode } from "Dashboards/WidgetContracts/WidgetProps";
import { WidgetHost } from "Dashboards/WidgetHost/WidgetHost";
import { ITfsPageService } from "Tfs/Platform/Page";
import { VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Checkbox } from "VSSUI/Checkbox";
import { ComboBox, IComboBoxOption } from "VSSUI/ComboBox";
import { TextField } from "VSSUI/TextField";
import { getTeam } from "./DashboardsSamplesContext"

interface WidgetHostSampleProps {}

interface OnScreenProps {
    contributionId: string;
    widgetName: string;

    rows: number;
    columns: number;

    customSettings: string | null;

    userCanConfigure?: boolean;
    openLinksInNewTab?: boolean;

    // Samples only prop for acting on events
    handleHostEvents?: boolean;
    catalogEntry: WidgetCatalogEntry;
}

interface WidgetHostSampleState {
    /** Not-yet committed props in the editor. Widget re-renders in sample only when user requests.  */
    onScreenProps: OnScreenProps;

    /** WidgetHostProps for Rendering */
    widgetHostProps: WidgetHostProps;

    iteration: number;

    widgetConfigContributionId: string | undefined;
    projectId: string | undefined;
}

/**
 * This sample provides a limited experience to evaluate WidgetHost outside of a dashboard context.
 */
export class WidgetHostSample extends VssComponent<WidgetHostSampleProps, WidgetHostSampleState> {
    private static widgetSamples: WidgetCatalogEntry[];

    // Widget Contribution Specific Customizations which are not currently retained on Contribution.
    private static widgetOverrides: { [contributionId: string]: Partial<OnScreenProps> } = generateWidgetCustomizations();

    public async componentDidMount(): Promise<void> {
        const pageServiceData = this.context.pageContext.getService<ITfsPageService>("ITfsPageService").getData();
        const projectId = (pageServiceData && pageServiceData.project && pageServiceData.project.id) || "";

        const queryCache = this.context.pageContext.getService<ICacheableQueryService>("IDashboardsCacheableQueryService");
        const query = new WidgetCatalogQuery(projectId);
        const catalogEntries = await queryCache.getCacheableQueryResult(query);
        if (catalogEntries != null) {
            WidgetHostSample.widgetSamples = catalogEntries.catalogEntries;
        }
        this.setState(this.getDefaultState());
    }

    public render(): JSX.Element | null {
        if (this.state == null) {
            return null;
        }
        const key = JSON.stringify({
            iteration: this.state.iteration,
            contributionId: this.state.widgetHostProps.widgetProps.contributionId
        });
        const widgetSize = WidgetSizeConverter.ConvertToPixelSize(this.state.widgetHostProps.widgetProps.size);

        // Note the Engineering sample should change to allow for explicit config experience.
        return (
            <div className="widgethost-sample-container" style={{ width: "800px" }}>
                <div>
                    <b>WidgetHost</b> allows for widgets to be rendered from a dashboard, or in-situ experience. Below, we see a WidgetHost containing
                    a Widget based on the selected Contribution Id. Edit experience can be initiated, but settings are not retained or rendered in
                    preview(Catalog & Config sample demonstrates these aspects).
                </div>
                <br />

                {this.renderWidgetConfig()}
                <div>Widget Preview</div>
                <br />
                <div className="dashboard widget-shadow-enabled" style={widgetSize}>
                    <WidgetHost key={key} {...this.state.widgetHostProps} />
                </div>
                {this.renderWidgetOptions()}
            </div>
        );
    }

    private getDefaultState(): WidgetHostSampleState {
        if (WidgetHostSample.widgetSamples === undefined) {
            throw new Error("unsupported scenario");
        } else {
            const onScreenProps: OnScreenProps = this.toOnScreenProps(WidgetHostSample.widgetSamples[0]);
            return {
                onScreenProps: onScreenProps,
                widgetHostProps: this.generateWidgetHostProps(onScreenProps),
                iteration: 0,
                widgetConfigContributionId: undefined,
                projectId: undefined
            };
        }
    }

    private toOnScreenProps(catalogEntry: WidgetCatalogEntry): OnScreenProps {
        const onsScreenProps: OnScreenProps = {
            widgetName: catalogEntry.name,
            contributionId: catalogEntry.contributionId,
            columns: catalogEntry.defaultSize.columnSpan,
            rows: catalogEntry.defaultSize.rowSpan,
            customSettings: null,
            userCanConfigure: true,
            openLinksInNewTab: false,
            handleHostEvents: false,
            catalogEntry: catalogEntry
        };
        Object.assign(onsScreenProps, WidgetHostSample.widgetOverrides[catalogEntry.contributionId]);
        return onsScreenProps;
    }

    private generateWidgetHostProps(onScreenProps: OnScreenProps): WidgetHostProps {
        const props: WidgetHostProps = {
            widgetPresentationMode: WidgetPresentationMode.dashboardView,
            parentPageName: "WidgetHostSample",
            widgetProps: {
                id: "76ccd6a4-8e12-43f7-b244-c4b07eef4e10",
                name: onScreenProps.widgetName,
                settings: onScreenProps.customSettings !== "" ? onScreenProps.customSettings : null, // Treat empty string as null, for reliable handling of unconfigured state in legacy Widgets.
                settingsVersion: { major: 1, minor: 0, patch: 0 },
                position: {
                    column: 1,
                    row: 1
                },
                size: {
                    // Grid is responsible for giving WidgetHost size in pixels. Modern Widgets don't care about grid size.
                    columnSpan: onScreenProps.columns,
                    rowSpan: onScreenProps.rows
                },
                contributionId: onScreenProps.contributionId,
                areSettingsBlockedForUser: false,
                configurationContributionId: onScreenProps.catalogEntry.configContributionId,
                isNameConfigurable: onScreenProps.catalogEntry.isNameConfigurable,
                isEnabled: true
            },
            convertToPixelSize: WidgetSizeConverter.ConvertToPixelSize,
            userCanConfigure: onScreenProps.userCanConfigure,
            openLinksInNewTab: onScreenProps.openLinksInNewTab,
            widgetHostParentProps: {
                onConfigureWidget: this.onConfigureWidget,
                onCopyWidget: this.onCopyWidget,
                onDeleteWidget: this.onDeleteWidget,
                onWidgetLoaded: this.onWidgetLoaded
            },
            teamContext: getTeam(this.context.pageContext)
        };
        return props;
    }

    private renderWidgetConfig() {
        const numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9];
        const rowColOptions: IComboBoxOption[] = numbers.map(n => {
            return { key: n, text: n.toString() };
        });
        const widgetOptions: IComboBoxOption[] = WidgetHostSample.widgetSamples.map(w => {
            return { key: w.contributionId, text: w.contributionId };
        });

        return (
            <div>
                <ComboBox
                    label={"Widget Contribution Id"}
                    options={widgetOptions}
                    text={this.state.onScreenProps.contributionId}
                    onChanged={this.onContributionChange}
                />
                <TextField label={"Widget Name"} value={this.state.onScreenProps.widgetName} onChange={this.onNameChange} />
                <ComboBox
                    label={"Columns"}
                    key={"columns"}
                    options={rowColOptions}
                    text={this.state.onScreenProps.columns.toString()}
                    onChanged={this.onColumnChange}
                />
                <ComboBox
                    label={"Rows"}
                    key={"rows"}
                    options={rowColOptions}
                    text={this.state.onScreenProps.rows.toString()}
                    onChanged={this.onRowChange}
                />
                <TextField
                    multiline
                    autoAdjustHeight
                    label={"Custom Settings"}
                    value={this.state.onScreenProps.customSettings || ""}
                    onChange={this.onCustomSettingsChange}
                />
                <Checkbox
                    label={"User can Configure & Delete Widget"}
                    checked={this.state.onScreenProps.userCanConfigure as boolean}
                    onChange={this.onEditToggle}
                />
                <Checkbox
                    label={"Open Links in New Tabs (Preference)"}
                    checked={this.state.onScreenProps.openLinksInNewTab as boolean}
                    onChange={this.onOpenLinksPrefToggle}
                />
                <Checkbox
                    label={"Display Host Events"}
                    checked={this.state.onScreenProps.handleHostEvents as boolean}
                    onChange={this.onEventHandlingToggle}
                />
                <br />

                <Button text={"Re-Render Widget"} onClick={() => this.runContribution()} />
            </div>
        );
    }

    private renderWidgetOptions() {
        return <TextField multiline label={"WidgetHostProps"} autoAdjustHeight value={JSON.stringify(this.state.widgetHostProps, null, "  ")} />;
    }

    private onContributionChange = (newValue: IComboBoxOption): void => {
        const catalogEntry: WidgetCatalogEntry | undefined = WidgetHostSample.widgetSamples.find(widget => {
            return widget.contributionId == newValue.key;
        });
        if (catalogEntry) {
            catalogEntry.contributionId = newValue.key.toString();
            const onScreenProps = this.toOnScreenProps(catalogEntry);
            this.setState({ onScreenProps: onScreenProps, widgetHostProps: this.generateWidgetHostProps(onScreenProps) });
        }
    };
    private onNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string): void => {
        const onScreenProps = this.state.onScreenProps;
        onScreenProps.widgetName = newValue;
        this.setState({ onScreenProps: onScreenProps });
    };
    private onRowChange = (option: IComboBoxOption): void => {
        const onScreenProps = this.state.onScreenProps;
        onScreenProps.rows = parseInt(option.text);
        this.setState({ onScreenProps: onScreenProps });
    };

    private onColumnChange = (option: IComboBoxOption): void => {
        const onScreenProps = this.state.onScreenProps;
        onScreenProps.columns = parseInt(option.text);
        this.setState({ onScreenProps: onScreenProps });
    };

    private onCustomSettingsChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, newValue: string): void => {
        const onScreenProps = this.state.onScreenProps;
        onScreenProps.customSettings = newValue;
        this.setState({ onScreenProps: onScreenProps });
    };

    private onEditToggle = (event: React.SyntheticEvent<HTMLElement>, checked: boolean): void => {
        const onScreenProps = this.state.onScreenProps;
        onScreenProps.userCanConfigure = checked;
        this.setState({ onScreenProps: onScreenProps });
    };

    private onOpenLinksPrefToggle = (event: React.SyntheticEvent<HTMLElement>, checked: boolean): void => {
        const onScreenProps = this.state.onScreenProps;
        onScreenProps.openLinksInNewTab = checked;
        this.setState({ onScreenProps: onScreenProps });
    };

    private onEventHandlingToggle = (event: React.SyntheticEvent<HTMLElement>, checked: boolean): void => {
        const onScreenProps = this.state.onScreenProps;
        onScreenProps.handleHostEvents = checked;
        this.setState({ onScreenProps: onScreenProps });
    };

    private runContribution(): void {
        const iteration = this.state.iteration + 1;
        this.setState({
            iteration: iteration,
            widgetHostProps: this.generateWidgetHostProps(this.state.onScreenProps)
        });
    }

    /** Event handlers from Widget Host to the consumer experience. */
    private onConfigureWidget = (widget: DashboardWidgetProps): void => {
        alert("Note: Config Scenarios are detailed in the Catalog-Config Sample.");
    };

    private onDeleteWidget = (widget: DashboardWidgetProps): void => {
        if (this.state.onScreenProps.handleHostEvents) {
            alert("Experience Notification - Delete Widget clicked for Id:" + widget.id);
        }
    };

    private onCopyWidget = (widget: WidgetCopyProps): void => {
        if (this.state.onScreenProps.handleHostEvents) {
            alert("Experience Notification - User has requested a copy of this widget to a destination dashboard.");
        }
    };

    private onWidgetLoaded = (widgetLoadedEventInfo: WidgetHostLoadedEventInfo): void => {
        if (this.state.onScreenProps.handleHostEvents) {
            alert(`Experience Notification - Widget Id loaded:${widgetLoadedEventInfo.widget} with outcome ${widgetLoadedEventInfo.outcome}`);
        }
    };
}

// Components.add("Dashboards-sample-view", DashboardsSamples);

/** Covers Customizations for testability convenience */
function generateWidgetCustomizations(): { [contributionId: string]: Partial<OnScreenProps> } {
    return {
        "ms.vss-dashboards-web.Microsoft.VisualStudioOnline.Dashboards.QueryScalarWidget": {
            customSettings: JSON.stringify(
                // Chances are these aren't the settings someone will want to test, but this gives the user a great starting point
                {
                    defaultBackgroundColor: "#292e6b",
                    queryId: "ed95b397-f773-4aa5-8954-ba05364a3021",
                    queryName: "Active Bugs",
                    colorRules: [
                        {
                            isEnabled: true,
                            backgroundColor: "#339947",
                            thresholdCount: 2,
                            operator: ">"
                        }
                    ],
                    lastArtifactName: "Active Bugs"
                },
                null,
                4
            )
        }
    };
}
